import { Check, ChevronDown, CircleAlert, Eye, EyeOff } from 'lucide-react';
import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '../../lib/cn.js';

/**
 * Campo é superfície preenchida, sem contorno (design.md §8).
 * O foco é marcado por filete de 1px e um tom a menos no preenchimento; o erro,
 * pelo fundo.
 */
const CONTROL_BASE =
  'w-full rounded-md px-3 text-sm text-foreground transition-colors duration-100 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-55';

const CONTROL_TONE = {
  // No foco o preenchimento desce um tom: é metade do sinal, junto do filete.
  rest: 'bg-muted hover:bg-accent focus:bg-accent',
  invalid: 'bg-danger-bg',
} as const;

interface FieldShellProps {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly required?: boolean | undefined;
  readonly children: (props: {
    id: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactNode;
}

/**
 * Casca comum de campo: label associado por `for`/`id`, texto de ajuda e erro
 * anunciados por `aria-describedby` — design.md §12.
 */
export function Field({ label, hint, error, required, children }: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-foreground">
        {label}
        {required ? <span className="ml-0.5 text-primary">*</span> : null}
      </label>

      {children({ id, describedBy: describedBy || undefined, invalid: Boolean(error) })}

      {hint && !error ? (
        <p id={hintId} className="text-[13px] text-muted-foreground">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="flex items-center gap-1.5 text-[13px] text-danger" role="alert">
          <CircleAlert size={14} strokeWidth={1.75} className="shrink-0" aria-hidden />
          {error}
        </p>
      ) : null}
    </div>
  );
}

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  /** Aplica a fonte monoespaçada — para valor, data, nº de contrato. */
  readonly mono?: boolean | undefined;
};

export function TextField({ label, hint, error, mono, className, ...rest }: TextFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={rest.required === true}
    >
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          className={cn(
            CONTROL_BASE,
            'h-10',
            mono && 'font-mono',
            invalid ? CONTROL_TONE.invalid : CONTROL_TONE.rest,
            className,
          )}
          {...rest}
        />
      )}
    </Field>
  );
}

type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
};

/**
 * Campo de senha com o olho para conferir o que se digitou.
 *
 * Senha em pontinhos é proteção contra quem olha por cima do ombro — e quem
 * troca a própria senha costuma estar sozinho, errando a digitação de uma frase
 * longa e sem saber onde. O olho devolve essa checagem sem baixar o padrão: o
 * campo nasce oculto, e revelar é ação deliberada de quem está ali.
 *
 * Só aparece quando há o que revelar. Num campo vazio seria enfeite, e enfeite
 * que parece botão é convite a um clique que não faz nada.
 */
export function PasswordField({ label, hint, error, className, ...rest }: PasswordFieldProps) {
  const [visivel, setVisivel] = useState(false);
  const temValor = rest.value === undefined || String(rest.value) !== '';

  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={rest.required === true}
    >
      {({ id, describedBy, invalid }) => (
        <div className="relative">
          <input
            id={id}
            type={visivel ? 'text' : 'password'}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            className={cn(
              CONTROL_BASE,
              'h-10 pr-10',
              // O Edge põe um olho próprio, e dois olhos no mesmo campo confundem.
              '[&::-ms-reveal]:hidden',
              invalid ? CONTROL_TONE.invalid : CONTROL_TONE.rest,
              className,
            )}
            {...rest}
          />

          {temValor ? (
            <button
              type="button"
              onClick={() => setVisivel((antes) => !antes)}
              aria-label={visivel ? 'Ocultar a senha' : 'Mostrar a senha'}
              aria-pressed={visivel}
              aria-controls={id}
              className="absolute inset-y-0 right-0 grid w-10 place-items-center rounded-r-md text-muted-foreground transition-colors duration-100 hover:text-foreground"
            >
              {visivel ? (
                <EyeOff size={16} strokeWidth={1.5} aria-hidden />
              ) : (
                <Eye size={16} strokeWidth={1.5} aria-hidden />
              )}
            </button>
          ) : null}
        </div>
      )}
    </Field>
  );
}

type TextAreaFieldProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
};

export function TextAreaField({ label, hint, error, className, ...rest }: TextAreaFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={rest.required === true}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          rows={rest.rows ?? 4}
          className={cn(
            CONTROL_BASE,
            'resize-y py-2.5 leading-relaxed',
            invalid ? CONTROL_TONE.invalid : CONTROL_TONE.rest,
            className,
          )}
          {...rest}
        />
      )}
    </Field>
  );
}

type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly children: ReactNode;
};

export function SelectField({
  label,
  hint,
  error,
  className,
  children,
  ...rest
}: SelectFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={rest.required === true}
    >
      {({ id, describedBy, invalid }) => (
        /*
         * A seta é nossa, não a do navegador.
         *
         * Cada sistema desenha a sua num tamanho e num alinhamento diferentes,
         * e nenhuma delas conversava com o resto — a do Chrome nem fica no
         * mesmo eixo dos chevrons que os outros seletores usam. Com
         * `appearance-none` o campo fica limpo e o ícone é posicionado por
         * nós, idêntico ao do seletor múltiplo.
         */
        <div className="relative">
          <select
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            className={cn(
              CONTROL_BASE,
              'h-10 cursor-pointer appearance-none pr-9',
              invalid ? CONTROL_TONE.invalid : CONTROL_TONE.rest,
              className,
            )}
            {...rest}
          >
            {children}
          </select>

          <ChevronDown
            size={15}
            strokeWidth={1.5}
            className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
        </div>
      )}
    </Field>
  );
}

type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> & {
  readonly label: string;
  readonly hint?: string | undefined;
};

/**
 * Caixa de seleção do sistema.
 *
 * O controle nativo é zerado com `appearance-none` e redesenhado como
 * superfície preenchida — cinza quando vazia, laranja quando marcada, sem
 * contorno (design.md §8). O ✓ é um ícone sobreposto, revelado pelo estado do
 * próprio input via `peer-checked`, para que teclado e leitor de tela
 * continuem interagindo com o `<input>` de verdade.
 */
export function Checkbox({ label, hint, className, ...rest }: CheckboxProps) {
  const id = useId();

  return (
    <div className="flex items-start gap-2.5">
      <span className="relative grid shrink-0 place-items-center">
        <input
          id={id}
          type="checkbox"
          className={cn(
            'peer size-[18px] cursor-pointer appearance-none rounded-2xs bg-muted transition-colors duration-100',
            'hover:bg-accent checked:bg-primary checked:hover:bg-primary-hover',
            'disabled:cursor-not-allowed disabled:opacity-55',
            className,
          )}
          {...rest}
        />
        <Check
          size={12}
          strokeWidth={3}
          className="pointer-events-none absolute text-primary-foreground opacity-0 peer-checked:opacity-100"
          aria-hidden
        />
      </span>

      {/* Desabilitado, o rótulo perde a mãozinha: prometer clique que não vem é pior que não prometer. */}
      <label
        htmlFor={id}
        className={cn(
          'text-sm leading-[18px]',
          rest.disabled ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer',
        )}
      >
        {label}
        {hint ? (
          <span className="mt-0.5 block text-[13px] text-muted-foreground">{hint}</span>
        ) : null}
      </label>
    </div>
  );
}

interface RadioOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly hint?: string | undefined;
}

/**
 * Botões de opção do sistema: uma escolha entre poucas, todas à vista.
 *
 * Mesmo desenho da caixa de seleção (design.md §8), redondo: superfície
 * preenchida `muted`, `primary` quando escolhida, com o ponto em
 * `primary-foreground` revelado por `peer-checked`. O `<input>` nativo continua
 * sendo o controle — as setas do teclado trocam a opção dentro do grupo, pelo
 * `name` compartilhado — e o `fieldset` dá ao leitor de tela o nome do grupo.
 */
export function RadioGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  readonly label: string;
  readonly value: T;
  readonly options: readonly RadioOption<T>[];
  readonly onChange: (value: T) => void;
  readonly disabled?: boolean | undefined;
}) {
  const name = useId();

  return (
    <fieldset className="flex flex-col gap-2.5" disabled={disabled}>
      <legend className="sr-only">{label}</legend>

      {options.map((option) => {
        const id = `${name}-${option.value}`;

        return (
          <div key={option.value} className="flex items-start gap-2.5">
            <span className="relative grid shrink-0 place-items-center">
              <input
                id={id}
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                disabled={disabled}
                onChange={() => onChange(option.value)}
                className={cn(
                  'peer size-[18px] cursor-pointer appearance-none rounded-full bg-muted transition-colors duration-100',
                  'hover:bg-accent checked:bg-primary checked:hover:bg-primary-hover',
                  'disabled:cursor-not-allowed disabled:opacity-55',
                )}
              />
              <span
                className="pointer-events-none absolute size-1.5 rounded-full bg-primary-foreground opacity-0 peer-checked:opacity-100"
                aria-hidden
              />
            </span>

            <label
              htmlFor={id}
              className={cn(
                'text-sm leading-[18px]',
                disabled ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer',
              )}
            >
              {option.label}
              {option.hint ? (
                <span className="mt-0.5 block text-[13px] text-muted-foreground">
                  {option.hint}
                </span>
              ) : null}
            </label>
          </div>
        );
      })}
    </fieldset>
  );
}

/**
 * Campo de busca sem label visível — usado nas barras de filtro,
 * onde o ícone e o placeholder já dizem o que é.
 */
export function SearchInput({
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <input
      type="search"
      className={cn(CONTROL_BASE, 'h-10', CONTROL_TONE.rest, className)}
      {...rest}
    />
  );
}
